import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { handleServerValidations } from "../../../../utils/commonfunction";
import { BulkAdd } from "../../services/reportPrice.services";
import { List as ReportList } from "../../services/report.services";

const AddReportPrice = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Report Prices", url: "/admin/report-price/list/1" },
        { title: "Add Prices", url: "" },
    ];

    useEffect(() => {
        // Fetch active reports for dropdown
        const formData = new FormData();
        formData.append("per_page", 1000);
        formData.append("search", JSON.stringify({ status: "Active" }));
        
        ReportList(formData)
            .then(res => {
                if (res?.data?.list) {
                    setReports(res.data.list);
                }
            })
            .catch(err => console.log(err));
    }, []);

    const validationSchema = Yup.object().shape({
        report_id: Yup.string().required("Report is required"),
        prices: Yup.array().of(
            Yup.object().shape({
                about: Yup.string().optional(),
                duration: Yup.string().required("Duration is required"),
                price_inr: Yup.number().required("INR Price is required").min(0, "Price must be positive"),
                price_usd: Yup.number().required("USD Price is required").min(0, "Price must be positive")
            })
        ).min(1, "At least one price is required")
    });

    const initialValues = {
        report_id: "",
        prices: [
            { about: "", duration: "", price_inr: "", price_usd: "" }
        ]
    };

    const handleSubmit = (values, { setSubmitting }) => {
        BulkAdd(values)
            .then((response) => {
                setSubmitting(false);
                if (response.status || response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    navigate("/admin/report-price/list/1");
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            })
            .catch((error) => {
                setSubmitting(false);
                Swal.fire({
                    icon: "error",
                    text: handleServerValidations(error),
                    ...SWAL_SETTINGS,
                });
                console.log(error);
            });
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between mb-3">
                            <h6 className="main-content-label">Add Report Prices</h6>
                            <Link to="/admin/report-price/list/1" className="btn btn-warning signbtn ripple">
                                <i className="fa fa-arrow-left"></i> {t("btn_back")}
                            </Link>
                        </div>
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ values, errors, touched, isSubmitting }) => (
                                <Form className="p-3">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <div className="form-group">
                                                <label>Select Report <span className="text-danger">*</span></label>
                                                <Field as="select" name="report_id" className="form-control">
                                                    <option value="">Select an active report</option>
                                                    {reports.map((report) => (
                                                        <option key={report.id || report._id} value={report.id || report._id}>
                                                            {report.title}
                                                        </option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="report_id" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <h6 className="mt-3 mb-3">Set Prices</h6>
                                    
                                    <FieldArray name="prices">
                                        {({ push, remove }) => (
                                            <div>
                                                {values.prices.map((price, index) => (
                                                    <div key={index} className="row align-items-center mb-3">
                                                        <div className="col-md-3">
                                                            <div className="form-group mb-0">
                                                                <label>About</label>
                                                                <Field name={`prices.${index}.about`} className="form-control" placeholder="Enter about info" />
                                                                <ErrorMessage name={`prices.${index}.about`} component="div" className="text-danger mt-1" />
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <div className="form-group mb-0">
                                                                <label>Duration <span className="text-danger">*</span></label>
                                                                <Field name={`prices.${index}.duration`} className="form-control" placeholder="e.g. 1 Year, 6 Months" />
                                                                <ErrorMessage name={`prices.${index}.duration`} component="div" className="text-danger mt-1" />
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label>Price (INR) <span className="text-danger">*</span></label>
                                                                <Field name={`prices.${index}.price_inr`} type="number" className="form-control" placeholder="INR" />
                                                                <ErrorMessage name={`prices.${index}.price_inr`} component="div" className="text-danger mt-1" />
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label>Price (USD) <span className="text-danger">*</span></label>
                                                                <Field name={`prices.${index}.price_usd`} type="number" className="form-control" placeholder="USD" />
                                                                <ErrorMessage name={`prices.${index}.price_usd`} component="div" className="text-danger mt-1" />
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            {values.prices.length > 1 && (
                                                                <button type="button" className="btn btn-danger mt-4" onClick={() => remove(index)}>
                                                                    <i className="fa fa-trash"></i> Remove
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary mt-2 mb-4"
                                                    onClick={() => push({ about: "", duration: "", price_inr: "", price_usd: "" })}
                                                >
                                                    <i className="fa fa-plus"></i> Add More
                                                </button>
                                            </div>
                                        )}
                                    </FieldArray>

                                    <div className="row mt-4">
                                        <div className="col-md-12 text-center">
                                            <button type="submit" className="btn btn-main-primary signbtn mr-2" disabled={isSubmitting}>
                                                {isSubmitting ? "Saving..." : "Save Prices"}
                                            </button>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddReportPrice;
